// File: src/components/event/EventBillingPanel.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/axios";
import toast from "react-hot-toast";
import {
  Receipt,
  Wallet,
  CreditCard,
  FilePlus2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  PoundSterling,
  ArrowRightLeft,
} from "lucide-react";

/* ───────────────── helpers ───────────────── */
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const toNumNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const safeStr = (v, d = "—") => {
  const s = v == null ? "" : String(v).trim();
  return s ? s : d;
};

const gbp = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(toNum(v, 0));

function asDateGB(v) {
  try {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
    return d.toLocaleDateString("en-GB");
  } catch {
    return v ? String(v).slice(0, 10) : "—";
  }
}

function asDateTimeGB(v) {
  try {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 19).replace("T", " ");
    return d.toLocaleString("en-GB");
  } catch {
    return v ? String(v).slice(0, 19).replace("T", " ") : "—";
  }
}

function normalizeMoneyInput(v) {
  const s = String(v ?? "").trim();
  if (!s) return "";
  return s.replace(/[^\d.-]/g, "");
}

function statusTone(status) {
  const s = String(status || "").toLowerCase().trim();
  if (s === "paid") return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (s === "part_paid") return "border-amber-200 bg-amber-50 text-amber-800";
  if (s === "issued") return "border-sky-200 bg-sky-50 text-sky-800";
  if (s === "draft") return "border-slate-200 bg-slate-100 text-slate-700";
  if (s === "cancelled") return "border-rose-200 bg-rose-50 text-rose-800";
  if (s === "credited") return "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800";
  return "border-slate-200 bg-slate-100 text-slate-700";
}

function humanizeStatus(status) {
  const s = String(status || "").toLowerCase().trim();
  if (!s) return "—";
  return s.replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function invoiceTypeLabel(v) {
  const s = String(v || "").toLowerCase().trim();
  if (!s) return "—";
  return s.replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

/* ───────────────── endpoint fallbacks ───────────────── */
async function getBillingSummaryWithFallbacks(eventId, config = {}) {
  try {
    const r = await api.get(`/event/${eventId}/billing-summary`, config);
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) throw e1;
    const r2 = await api.get(`/events/${eventId}/billing-summary`, config);
    return r2.data;
  }
}

async function getInvoicesWithFallbacks(eventId, config = {}) {
  try {
    const r = await api.get(`/event/${eventId}/invoices`, config);
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) throw e1;
    const r2 = await api.get(`/events/${eventId}/invoices`, config);
    return r2.data;
  }
}

async function getInvoiceDetailWithFallbacks(eventId, invoiceId, config = {}) {
  try {
    const r = await api.get(`/event/${eventId}/invoices/${invoiceId}`, config);
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) throw e1;
    const r2 = await api.get(`/events/${eventId}/invoices/${invoiceId}`, config);
    return r2.data;
  }
}

async function createInvoiceWithFallbacks(eventId, payload) {
  try {
    const r = await api.post(`/event/${eventId}/invoices`, payload);
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) throw e1;
    const r2 = await api.post(`/events/${eventId}/invoices`, payload);
    return r2.data;
  }
}

async function generateInvoiceFromCommercialWithFallbacks(eventId, payload) {
  try {
    const r = await api.post(`/event/${eventId}/invoices/generate-from-commercial`, payload);
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) throw e1;
    const r2 = await api.post(`/events/${eventId}/invoices/generate-from-commercial`, payload);
    return r2.data;
  }
}

async function updateInvoiceWithFallbacks(eventId, invoiceId, payload) {
  try {
    const r = await api.put(`/event/${eventId}/invoices/${invoiceId}`, payload);
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) throw e1;
    const r2 = await api.put(`/events/${eventId}/invoices/${invoiceId}`, payload);
    return r2.data;
  }
}

async function issueInvoiceWithFallbacks(eventId, invoiceId, payload = {}) {
  try {
    const r = await api.post(`/event/${eventId}/invoices/${invoiceId}/issue`, payload);
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) throw e1;
    const r2 = await api.post(`/events/${eventId}/invoices/${invoiceId}/issue`, payload);
    return r2.data;
  }
}

async function cancelInvoiceWithFallbacks(eventId, invoiceId, payload = {}) {
  try {
    const r = await api.post(`/event/${eventId}/invoices/${invoiceId}/cancel`, payload);
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) throw e1;
    const r2 = await api.post(`/events/${eventId}/invoices/${invoiceId}/cancel`, payload);
    return r2.data;
  }
}

async function getInvoicePaymentsWithFallbacks(eventId, invoiceId, config = {}) {
  try {
    const r = await api.get(`/event/${eventId}/invoices/${invoiceId}/payments`, config);
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) throw e1;
    const r2 = await api.get(`/events/${eventId}/invoices/${invoiceId}/payments`, config);
    return r2.data;
  }
}

async function createInvoicePaymentWithFallbacks(eventId, invoiceId, payload) {
  try {
    const r = await api.post(`/event/${eventId}/invoices/${invoiceId}/payments`, payload);
    return r.data;
  } catch (e1) {
    if (e1?.response?.status !== 404) throw e1;
    const r2 = await api.post(`/events/${eventId}/invoices/${invoiceId}/payments`, payload);
    return r2.data;
  }
}

/* ───────────────── main ───────────────── */
export default function EventBillingPanel({ eventId }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [billingSummary, setBillingSummary] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);

  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoicePayments, setSelectedInvoicePayments] = useState([]);
  const [selectedInvoiceLoading, setSelectedInvoiceLoading] = useState(false);

  const [createMode, setCreateMode] = useState("manual");
  const [busyAction, setBusyAction] = useState("");

  const [manualForm, setManualForm] = useState({
    invoice_type: "final",
    status: "draft",
    invoice_number: "",
    issue_date: "",
    due_date: "",
    subtotal: "",
    tax_amount: "",
    total_amount: "",
    linked_transaction_type: "payment",
    notes: "",
    pdf_path: "",
  });

  const [commercialForm, setCommercialForm] = useState({
    invoice_type: "final",
    invoice_number: "",
    issue_date: "",
    due_date: "",
    subtotal: "",
    tax_amount: "",
    total_amount: "",
    linked_transaction_type: "payment",
    notes: "",
    pdf_path: "",
  });

  const [paymentForm, setPaymentForm] = useState({
    payment_direction: "in",
    amount: "",
    payment_method: "",
    reference: "",
    notes: "",
    paid_at: "",
    linked_transaction_type: "payment",
  });

  const refreshAll = useCallback(
    async ({ preserveSelected = true } = {}) => {
      if (!eventId) return;

      try {
        if (loading) {
          // first load handled below
        } else {
          setRefreshing(true);
        }

        const [summaryRes, invoicesRes] = await Promise.all([
          getBillingSummaryWithFallbacks(eventId),
          getInvoicesWithFallbacks(eventId),
        ]);

        setBillingSummary(summaryRes || null);
        const nextItems = Array.isArray(invoicesRes?.items) ? invoicesRes.items : [];
        setInvoiceItems(nextItems);

        const nextSelectedId =
          preserveSelected && selectedInvoiceId
            ? nextItems.find((x) => Number(x.event_invoice_id) === Number(selectedInvoiceId))
              ? selectedInvoiceId
              : nextItems[0]?.event_invoice_id ?? null
            : nextItems[0]?.event_invoice_id ?? null;

        setSelectedInvoiceId(nextSelectedId ?? null);
      } catch (e) {
        console.error("Failed to load billing panel:", e);
        toast.error(e?.response?.data?.error || e?.message || "Failed to load billing data");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [eventId, loading, selectedInvoiceId]
  );

  const loadSelectedInvoice = useCallback(async () => {
    if (!eventId || !selectedInvoiceId) {
      setSelectedInvoice(null);
      setSelectedInvoicePayments([]);
      return;
    }

    setSelectedInvoiceLoading(true);
    try {
      const [detailRes, paymentsRes] = await Promise.all([
        getInvoiceDetailWithFallbacks(eventId, selectedInvoiceId),
        getInvoicePaymentsWithFallbacks(eventId, selectedInvoiceId),
      ]);

      setSelectedInvoice(detailRes?.invoice || null);
      setSelectedInvoicePayments(Array.isArray(paymentsRes?.payments) ? paymentsRes.payments : []);
    } catch (e) {
      console.error("Failed to load invoice detail:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to load invoice detail");
      setSelectedInvoice(null);
      setSelectedInvoicePayments([]);
    } finally {
      setSelectedInvoiceLoading(false);
    }
  }, [eventId, selectedInvoiceId]);

  useEffect(() => {
    refreshAll({ preserveSelected: false });
  }, [refreshAll]);

  useEffect(() => {
    loadSelectedInvoice();
  }, [loadSelectedInvoice]);

  const summaryCards = useMemo(() => {
    const s = billingSummary?.invoice_summary || {};
    const commercialHeader = billingSummary?.commercial_header || {};
    const commercialTxSummary = billingSummary?.commercial_transactions_summary || {};

    return [
      {
        title: "Invoices Count",
        value: `${toNum(s.invoices_count, 0)}`,
        subtitle: "Client billing documents on this event",
      },
      {
        title: "Total Invoiced",
        value: gbp(s.total_invoiced),
        subtitle: "Sum of event invoice totals",
      },
      {
        title: "Invoice Receipts",
        value: gbp(s.total_received_against_invoices),
        subtitle: "Payments recorded against invoices",
      },
      {
        title: "Invoice Outstanding",
        value: gbp(s.total_outstanding_invoices),
        subtitle: "Remaining due across invoices",
      },
      {
        title: "Commercial Quote",
        value: gbp(commercialHeader?.client_total_quote),
        subtitle: "Commercial header quote total",
      },
      {
        title: "Commercial Net Cash",
        value: gbp(commercialTxSummary?.net_received),
        subtitle: "Commercial transactions net received",
      },
    ];
  }, [billingSummary]);

  const selectedInvoiceFinancials = useMemo(() => {
    const totals = selectedInvoice?.totals || {};
    return {
      total_in: toNum(totals.total_in, 0),
      total_out: toNum(totals.total_out, 0),
      net_paid: toNum(totals.net_paid, 0),
      remaining: toNum(totals.remaining, 0),
      derived_status: selectedInvoice?.derived_status || selectedInvoice?.status || "draft",
    };
  }, [selectedInvoice]);

  const handleManualCreate = async () => {
    try {
      setBusyAction("create-manual");

      const payload = {
        invoice_type: manualForm.invoice_type,
        status: manualForm.status,
        invoice_number: manualForm.invoice_number || null,
        issue_date: manualForm.issue_date || null,
        due_date: manualForm.due_date || null,
        subtotal: manualForm.subtotal === "" ? 0 : Number(manualForm.subtotal),
        tax_amount: manualForm.tax_amount === "" ? 0 : Number(manualForm.tax_amount),
        total_amount: manualForm.total_amount === "" ? null : Number(manualForm.total_amount),
        linked_transaction_type: manualForm.linked_transaction_type || null,
        notes: manualForm.notes || null,
        pdf_path: manualForm.pdf_path || null,
      };

      await createInvoiceWithFallbacks(eventId, payload);
      toast.success("Invoice created");

      setManualForm({
        invoice_type: "final",
        status: "draft",
        invoice_number: "",
        issue_date: "",
        due_date: "",
        subtotal: "",
        tax_amount: "",
        total_amount: "",
        linked_transaction_type: "payment",
        notes: "",
        pdf_path: "",
      });

      await refreshAll({ preserveSelected: false });
    } catch (e) {
      console.error("Create invoice failed:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to create invoice");
    } finally {
      setBusyAction("");
    }
  };

  const handleGenerateFromCommercial = async () => {
    try {
      setBusyAction("generate-commercial");

      const payload = {
        invoice_type: commercialForm.invoice_type,
        invoice_number: commercialForm.invoice_number || null,
        issue_date: commercialForm.issue_date || null,
        due_date: commercialForm.due_date || null,
        subtotal: commercialForm.subtotal === "" ? null : Number(commercialForm.subtotal),
        tax_amount: commercialForm.tax_amount === "" ? null : Number(commercialForm.tax_amount),
        total_amount: commercialForm.total_amount === "" ? null : Number(commercialForm.total_amount),
        linked_transaction_type: commercialForm.linked_transaction_type || null,
        notes: commercialForm.notes || null,
        pdf_path: commercialForm.pdf_path || null,
      };

      await generateInvoiceFromCommercialWithFallbacks(eventId, payload);
      toast.success("Invoice generated from commercial");

      setCommercialForm({
        invoice_type: "final",
        invoice_number: "",
        issue_date: "",
        due_date: "",
        subtotal: "",
        tax_amount: "",
        total_amount: "",
        linked_transaction_type: "payment",
        notes: "",
        pdf_path: "",
      });

      await refreshAll({ preserveSelected: false });
    } catch (e) {
      console.error("Generate from commercial failed:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to generate from commercial");
    } finally {
      setBusyAction("");
    }
  };

  const handleIssueInvoice = async () => {
    if (!selectedInvoice?.event_invoice_id) return;

    try {
      setBusyAction("issue");
      await issueInvoiceWithFallbacks(eventId, selectedInvoice.event_invoice_id, {});
      toast.success("Invoice issued");
      await refreshAll({ preserveSelected: true });
      await loadSelectedInvoice();
    } catch (e) {
      console.error("Issue invoice failed:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to issue invoice");
    } finally {
      setBusyAction("");
    }
  };

  const handleCancelInvoice = async () => {
    if (!selectedInvoice?.event_invoice_id) return;

    try {
      setBusyAction("cancel");
      await cancelInvoiceWithFallbacks(eventId, selectedInvoice.event_invoice_id, {});
      toast.success("Invoice cancelled");
      await refreshAll({ preserveSelected: true });
      await loadSelectedInvoice();
    } catch (e) {
      console.error("Cancel invoice failed:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to cancel invoice");
    } finally {
      setBusyAction("");
    }
  };

  const handleQuickUpdateInvoice = async () => {
    if (!selectedInvoice?.event_invoice_id) return;

    try {
      setBusyAction("update");

      const payload = {
        invoice_type: selectedInvoice.invoice_type,
        status: selectedInvoice.status,
        invoice_number: selectedInvoice.invoice_number,
        issue_date: selectedInvoice.issue_date || null,
        due_date: selectedInvoice.due_date || null,
        subtotal: toNumNull(selectedInvoice.subtotal),
        tax_amount: toNumNull(selectedInvoice.tax_amount),
        total_amount: toNumNull(selectedInvoice.total_amount),
        linked_transaction_type: selectedInvoice.linked_transaction_type || null,
        pdf_path: selectedInvoice.pdf_path || null,
        email_sent_at: selectedInvoice.email_sent_at || null,
        notes: selectedInvoice.notes || null,
        client_id: selectedInvoice.client_id || null,
      };

      await updateInvoiceWithFallbacks(eventId, selectedInvoice.event_invoice_id, payload);
      toast.success("Invoice updated");
      await refreshAll({ preserveSelected: true });
      await loadSelectedInvoice();
    } catch (e) {
      console.error("Update invoice failed:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to update invoice");
    } finally {
      setBusyAction("");
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedInvoice?.event_invoice_id) return;

    const amount = Number(paymentForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Payment amount must be greater than 0");
      return;
    }

    try {
      setBusyAction("payment");

      const payload = {
        payment_direction: paymentForm.payment_direction,
        amount,
        payment_method: paymentForm.payment_method || null,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null,
        paid_at: paymentForm.paid_at || null,
        linked_transaction_type: paymentForm.linked_transaction_type || null,
      };

      await createInvoicePaymentWithFallbacks(eventId, selectedInvoice.event_invoice_id, payload);
      toast.success("Invoice payment recorded");

      setPaymentForm({
        payment_direction: "in",
        amount: "",
        payment_method: "",
        reference: "",
        notes: "",
        paid_at: "",
        linked_transaction_type:
          selectedInvoice?.linked_transaction_type ||
          (selectedInvoice?.invoice_type === "deposit" ? "deposit" : "payment"),
      });

      await refreshAll({ preserveSelected: true });
      await loadSelectedInvoice();
    } catch (e) {
      console.error("Record payment failed:", e);
      toast.error(e?.response?.data?.error || e?.message || "Failed to record payment");
    } finally {
      setBusyAction("");
    }
  };

  useEffect(() => {
    if (!selectedInvoice) return;
    setPaymentForm((prev) => ({
      ...prev,
      linked_transaction_type:
        selectedInvoice?.linked_transaction_type ||
        (selectedInvoice?.invoice_type === "deposit" ? "deposit" : "payment"),
    }));
  }, [selectedInvoice]);

  if (loading) {
    return (
      <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm text-slate-700">
        Loading billing panel…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {refreshing ? (
        <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">
          Refreshing billing data…
        </div>
      ) : null}

      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-950">Billing</h2>
            <div className="mt-1 text-sm text-slate-600">
              Event-side client invoicing, payments, commercial linkage, and invoice balances.
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => refreshAll({ preserveSelected: true })}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              type="button"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {summaryCards.map((card) => (
            <MetricCard
              key={card.title}
              title={card.title}
              value={card.value}
              subtitle={card.subtitle}
            />
          ))}
        </div>
      </section>

      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCreateMode("manual")}
            className={
              createMode === "manual"
                ? "rounded-2xl border border-slate-900 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
                : "rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white"
            }
            type="button"
          >
            Manual Invoice
          </button>

          <button
            onClick={() => setCreateMode("commercial")}
            className={
              createMode === "commercial"
                ? "rounded-2xl border border-slate-900 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
                : "rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white"
            }
            type="button"
          >
            Generate From Commercial
          </button>
        </div>

        {createMode === "manual" ? (
          <div className="mt-5">
            <FormSectionTitle
              icon={FilePlus2}
              title="Create Manual Invoice"
              subtitle="Create a client invoice directly against this event."
            />

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <FieldSelect
                label="Invoice Type"
                value={manualForm.invoice_type}
                onChange={(v) => setManualForm((p) => ({ ...p, invoice_type: v }))}
                options={["deposit", "interim", "final", "adjustment", "credit_note"]}
              />

              <FieldSelect
                label="Status"
                value={manualForm.status}
                onChange={(v) => setManualForm((p) => ({ ...p, status: v }))}
                options={["draft", "issued", "part_paid", "paid", "cancelled", "credited"]}
              />

              <FieldInput
                label="Invoice Number"
                value={manualForm.invoice_number}
                onChange={(v) => setManualForm((p) => ({ ...p, invoice_number: v }))}
                placeholder="Optional auto-generated if blank"
              />

              <FieldInput
                label="Issue Date"
                type="date"
                value={manualForm.issue_date}
                onChange={(v) => setManualForm((p) => ({ ...p, issue_date: v }))}
              />

              <FieldInput
                label="Due Date"
                type="date"
                value={manualForm.due_date}
                onChange={(v) => setManualForm((p) => ({ ...p, due_date: v }))}
              />

              <FieldSelect
                label="Linked Transaction Type"
                value={manualForm.linked_transaction_type}
                onChange={(v) => setManualForm((p) => ({ ...p, linked_transaction_type: v }))}
                options={["deposit", "payment", "refund", "charge"]}
              />

              <FieldInput
                label="Subtotal (£)"
                type="number"
                value={manualForm.subtotal}
                onChange={(v) => setManualForm((p) => ({ ...p, subtotal: normalizeMoneyInput(v) }))}
              />

              <FieldInput
                label="Tax Amount (£)"
                type="number"
                value={manualForm.tax_amount}
                onChange={(v) => setManualForm((p) => ({ ...p, tax_amount: normalizeMoneyInput(v) }))}
              />

              <FieldInput
                label="Total Amount (£)"
                type="number"
                value={manualForm.total_amount}
                onChange={(v) => setManualForm((p) => ({ ...p, total_amount: normalizeMoneyInput(v) }))}
                placeholder="Leave blank to let backend derive from subtotal + tax"
              />

              <div className="md:col-span-2 xl:col-span-3">
                <FieldInput
                  label="PDF Path"
                  value={manualForm.pdf_path}
                  onChange={(v) => setManualForm((p) => ({ ...p, pdf_path: v }))}
                  placeholder="/uploads/event-invoices/..."
                />
              </div>

              <div className="md:col-span-2 xl:col-span-3">
                <FieldTextarea
                  label="Notes"
                  value={manualForm.notes}
                  onChange={(v) => setManualForm((p) => ({ ...p, notes: v }))}
                />
              </div>
            </div>

            <div className="mt-4">
              <ActionButton
                icon={FilePlus2}
                label={busyAction === "create-manual" ? "Creating..." : "Create Invoice"}
                onClick={handleManualCreate}
                disabled={busyAction === "create-manual"}
              />
            </div>
          </div>
        ) : null}

        {createMode === "commercial" ? (
          <div className="mt-5">
            <FormSectionTitle
              icon={Receipt}
              title="Generate From Commercial"
              subtitle="Use current event commercial values to create a billing document."
            />

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <FieldSelect
                label="Invoice Type"
                value={commercialForm.invoice_type}
                onChange={(v) => setCommercialForm((p) => ({ ...p, invoice_type: v }))}
                options={["deposit", "interim", "final", "adjustment", "credit_note"]}
              />

              <FieldInput
                label="Invoice Number"
                value={commercialForm.invoice_number}
                onChange={(v) => setCommercialForm((p) => ({ ...p, invoice_number: v }))}
                placeholder="Optional auto-generated if blank"
              />

              <FieldSelect
                label="Linked Transaction Type"
                value={commercialForm.linked_transaction_type}
                onChange={(v) => setCommercialForm((p) => ({ ...p, linked_transaction_type: v }))}
                options={["deposit", "payment", "refund", "charge"]}
              />

              <FieldInput
                label="Issue Date"
                type="date"
                value={commercialForm.issue_date}
                onChange={(v) => setCommercialForm((p) => ({ ...p, issue_date: v }))}
              />

              <FieldInput
                label="Due Date"
                type="date"
                value={commercialForm.due_date}
                onChange={(v) => setCommercialForm((p) => ({ ...p, due_date: v }))}
              />

              <FieldInput
                label="Total Amount Override (£)"
                type="number"
                value={commercialForm.total_amount}
                onChange={(v) => setCommercialForm((p) => ({ ...p, total_amount: normalizeMoneyInput(v) }))}
              />

              <FieldInput
                label="Subtotal Override (£)"
                type="number"
                value={commercialForm.subtotal}
                onChange={(v) => setCommercialForm((p) => ({ ...p, subtotal: normalizeMoneyInput(v) }))}
              />

              <FieldInput
                label="Tax Override (£)"
                type="number"
                value={commercialForm.tax_amount}
                onChange={(v) => setCommercialForm((p) => ({ ...p, tax_amount: normalizeMoneyInput(v) }))}
              />

              <FieldInput
                label="PDF Path"
                value={commercialForm.pdf_path}
                onChange={(v) => setCommercialForm((p) => ({ ...p, pdf_path: v }))}
                placeholder="/uploads/event-invoices/..."
              />

              <div className="md:col-span-2 xl:col-span-3">
                <FieldTextarea
                  label="Notes"
                  value={commercialForm.notes}
                  onChange={(v) => setCommercialForm((p) => ({ ...p, notes: v }))}
                />
              </div>
            </div>

            <div className="mt-4">
              <ActionButton
                icon={Receipt}
                label={busyAction === "generate-commercial" ? "Generating..." : "Generate Invoice"}
                onClick={handleGenerateFromCommercial}
                disabled={busyAction === "generate-commercial"}
              />
            </div>
          </div>
        ) : null}
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-xl font-bold text-slate-950">Invoices</h3>
              <div className="mt-1 text-sm text-slate-600">
                Event invoices, current balances, and derived payment status.
              </div>
            </div>
            <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
              {invoiceItems.length} total
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {invoiceItems.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                No invoices created for this event yet.
              </div>
            ) : null}

            {invoiceItems.map((item) => {
              const active = Number(item.event_invoice_id) === Number(selectedInvoiceId);
              const st = item.derived_status || item.status || "draft";

              return (
                <button
                  key={item.event_invoice_id}
                  onClick={() => setSelectedInvoiceId(item.event_invoice_id)}
                  className={
                    active
                      ? "w-full rounded-2xl border border-slate-900 bg-slate-900 p-4 text-left text-white shadow-sm"
                      : "w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left text-slate-900 shadow-sm hover:bg-white"
                  }
                  type="button"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-bold">
                        {safeStr(item.invoice_number, `Invoice #${item.event_invoice_id}`)}
                      </div>
                      <div className={active ? "mt-1 text-xs text-slate-300" : "mt-1 text-xs text-slate-500"}>
                        {invoiceTypeLabel(item.invoice_type)} • Issue {asDateGB(item.issue_date)} • Due {asDateGB(item.due_date)}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={
                          active
                            ? "rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-white"
                            : `rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusTone(st)}`
                        }
                      >
                        {humanizeStatus(st)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                    <MiniStat
                      active={active}
                      label="Total"
                      value={gbp(item.total_amount)}
                    />
                    <MiniStat
                      active={active}
                      label="Net Paid"
                      value={gbp(item?.totals?.net_paid)}
                    />
                    <MiniStat
                      active={active}
                      label="Remaining"
                      value={gbp(item?.totals?.remaining)}
                    />
                    <MiniStat
                      active={active}
                      label="Client"
                      value={safeStr(item.client_name)}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h3 className="text-xl font-bold text-slate-950">Invoice Detail</h3>
              <div className="mt-1 text-sm text-slate-600">
                Payment history, invoice edits, issuing, cancelling, and recording receipts.
              </div>
            </div>

            {selectedInvoice ? (
              <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(selectedInvoiceFinancials.derived_status)}`}>
                {humanizeStatus(selectedInvoiceFinancials.derived_status)}
              </span>
            ) : null}
          </div>

          {!selectedInvoiceId ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              Select an invoice to view full billing detail.
            </div>
          ) : selectedInvoiceLoading ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              Loading invoice detail…
            </div>
          ) : !selectedInvoice ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              Invoice detail could not be loaded.
            </div>
          ) : (
            <div className="mt-4 space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <MetricCard title="Invoice Total" value={gbp(selectedInvoice.total_amount)} subtitle="Saved invoice total" />
                <MetricCard title="Payments In" value={gbp(selectedInvoiceFinancials.total_in)} subtitle="Receipts against this invoice" />
                <MetricCard title="Refunds Out" value={gbp(selectedInvoiceFinancials.total_out)} subtitle="Amounts returned against this invoice" />
                <MetricCard title="Remaining" value={gbp(selectedInvoiceFinancials.remaining)} subtitle="Outstanding balance" />
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="text-sm font-semibold text-slate-950">Invoice Header</div>

                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <FieldInput
                    label="Invoice Number"
                    value={selectedInvoice.invoice_number || ""}
                    onChange={(v) => setSelectedInvoice((p) => ({ ...p, invoice_number: v }))}
                  />

                  <FieldSelect
                    label="Invoice Type"
                    value={selectedInvoice.invoice_type || "final"}
                    onChange={(v) => setSelectedInvoice((p) => ({ ...p, invoice_type: v }))}
                    options={["deposit", "interim", "final", "adjustment", "credit_note"]}
                  />

                  <FieldSelect
                    label="Status"
                    value={selectedInvoice.status || "draft"}
                    onChange={(v) => setSelectedInvoice((p) => ({ ...p, status: v }))}
                    options={["draft", "issued", "part_paid", "paid", "cancelled", "credited"]}
                  />

                  <FieldInput
                    label="Issue Date"
                    type="date"
                    value={selectedInvoice.issue_date || ""}
                    onChange={(v) => setSelectedInvoice((p) => ({ ...p, issue_date: v }))}
                  />

                  <FieldInput
                    label="Due Date"
                    type="date"
                    value={selectedInvoice.due_date || ""}
                    onChange={(v) => setSelectedInvoice((p) => ({ ...p, due_date: v }))}
                  />

                  <FieldSelect
                    label="Linked Transaction Type"
                    value={selectedInvoice.linked_transaction_type || "payment"}
                    onChange={(v) => setSelectedInvoice((p) => ({ ...p, linked_transaction_type: v }))}
                    options={["deposit", "payment", "refund", "charge"]}
                  />

                  <FieldInput
                    label="Subtotal (£)"
                    type="number"
                    value={selectedInvoice.subtotal ?? ""}
                    onChange={(v) => setSelectedInvoice((p) => ({ ...p, subtotal: normalizeMoneyInput(v) }))}
                  />

                  <FieldInput
                    label="Tax Amount (£)"
                    type="number"
                    value={selectedInvoice.tax_amount ?? ""}
                    onChange={(v) => setSelectedInvoice((p) => ({ ...p, tax_amount: normalizeMoneyInput(v) }))}
                  />

                  <FieldInput
                    label="Total Amount (£)"
                    type="number"
                    value={selectedInvoice.total_amount ?? ""}
                    onChange={(v) => setSelectedInvoice((p) => ({ ...p, total_amount: normalizeMoneyInput(v) }))}
                  />

                  <div className="md:col-span-2 xl:col-span-3">
                    <FieldInput
                      label="PDF Path"
                      value={selectedInvoice.pdf_path || ""}
                      onChange={(v) => setSelectedInvoice((p) => ({ ...p, pdf_path: v }))}
                    />
                  </div>

                  <div className="md:col-span-2 xl:col-span-3">
                    <FieldTextarea
                      label="Notes"
                      value={selectedInvoice.notes || ""}
                      onChange={(v) => setSelectedInvoice((p) => ({ ...p, notes: v }))}
                    />
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <ActionButton
                    icon={Receipt}
                    label={busyAction === "update" ? "Saving..." : "Save Invoice"}
                    onClick={handleQuickUpdateInvoice}
                    disabled={busyAction === "update"}
                  />

                  <ActionButton
                    icon={CheckCircle2}
                    label={busyAction === "issue" ? "Issuing..." : "Issue Invoice"}
                    onClick={handleIssueInvoice}
                    disabled={busyAction === "issue" || selectedInvoice?.status === "cancelled"}
                    variant="emerald"
                  />

                  <ActionButton
                    icon={XCircle}
                    label={busyAction === "cancel" ? "Cancelling..." : "Cancel Invoice"}
                    onClick={handleCancelInvoice}
                    disabled={busyAction === "cancel"}
                    variant="rose"
                  />
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-950">Record Payment / Refund</div>
                    <div className="mt-1 text-xs text-slate-500">
                      This also mirrors to commercial transactions when linked transaction type is supplied.
                    </div>
                  </div>
                  <ArrowRightLeft className="h-4 w-4 text-slate-500" />
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <FieldSelect
                    label="Direction"
                    value={paymentForm.payment_direction}
                    onChange={(v) => setPaymentForm((p) => ({ ...p, payment_direction: v }))}
                    options={["in", "out"]}
                  />

                  <FieldInput
                    label="Amount (£)"
                    type="number"
                    value={paymentForm.amount}
                    onChange={(v) => setPaymentForm((p) => ({ ...p, amount: normalizeMoneyInput(v) }))}
                  />

                  <FieldSelect
                    label="Linked Commercial Tx"
                    value={paymentForm.linked_transaction_type || "payment"}
                    onChange={(v) => setPaymentForm((p) => ({ ...p, linked_transaction_type: v }))}
                    options={["deposit", "payment", "refund", "charge"]}
                  />

                  <FieldInput
                    label="Payment Method"
                    value={paymentForm.payment_method}
                    onChange={(v) => setPaymentForm((p) => ({ ...p, payment_method: v }))}
                    placeholder="bank / card / cash"
                  />

                  <FieldInput
                    label="Reference"
                    value={paymentForm.reference}
                    onChange={(v) => setPaymentForm((p) => ({ ...p, reference: v }))}
                    placeholder="receipt / txn ref"
                  />

                  <FieldInput
                    label="Paid At"
                    type="datetime-local"
                    value={paymentForm.paid_at}
                    onChange={(v) => setPaymentForm((p) => ({ ...p, paid_at: v }))}
                  />

                  <div className="md:col-span-2 xl:col-span-3">
                    <FieldTextarea
                      label="Notes"
                      value={paymentForm.notes}
                      onChange={(v) => setPaymentForm((p) => ({ ...p, notes: v }))}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <ActionButton
                    icon={CreditCard}
                    label={busyAction === "payment" ? "Recording..." : "Record Invoice Payment"}
                    onClick={handleRecordPayment}
                    disabled={busyAction === "payment"}
                    variant="sky"
                  />
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="text-sm font-semibold text-slate-950">Payment History</div>

                {selectedInvoicePayments.length === 0 ? (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                    No payments recorded against this invoice yet.
                  </div>
                ) : (
                  <div className="mt-4 overflow-auto rounded-2xl border border-slate-200 bg-white">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-slate-700">
                        <tr>
                          <th className="px-3 py-2 text-left">When</th>
                          <th className="px-3 py-2 text-left">Direction</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                          <th className="px-3 py-2 text-left">Method</th>
                          <th className="px-3 py-2 text-left">Reference</th>
                          <th className="px-3 py-2 text-left">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {selectedInvoicePayments.map((p) => {
                          const dir = String(p.payment_direction || "").toLowerCase();
                          const amount = toNum(p.amount, 0);
                          const negative = dir === "out";
                          return (
                            <tr key={p.payment_id} className="text-slate-800">
                              <td className="px-3 py-2 text-slate-500">{asDateTimeGB(p.paid_at || p.created_at)}</td>
                              <td className="px-3 py-2">{safeStr(dir)}</td>
                              <td className={`px-3 py-2 text-right font-semibold ${negative ? "text-rose-700" : "text-emerald-700"}`}>
                                {negative ? `- ${gbp(amount)}` : gbp(amount)}
                              </td>
                              <td className="px-3 py-2">{safeStr(p.payment_method)}</td>
                              <td className="px-3 py-2">{safeStr(p.reference)}</td>
                              <td className="px-3 py-2">{safeStr(p.notes)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ───────────────── ui helpers ───────────────── */
function MetricCard({ title, value, subtitle }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="mt-2 text-2xl font-bold text-slate-950">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
    </div>
  );
}

function MiniStat({ label, value, active = false }) {
  return (
    <div className={active ? "rounded-xl border border-white/15 bg-white/10 p-2.5" : "rounded-xl border border-slate-200 bg-white p-2.5"}>
      <div className={active ? "text-[11px] text-slate-300" : "text-[11px] text-slate-500"}>{label}</div>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}

function FormSectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <Icon className="h-4 w-4 text-slate-700" />
      </div>
      <div>
        <div className="text-lg font-semibold text-slate-950">{title}</div>
        <div className="mt-1 text-sm text-slate-600">{subtitle}</div>
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className="mb-1 block text-sm text-slate-600">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-300 bg-white p-2.5 text-slate-950"
      />
    </div>
  );
}

function FieldTextarea({ label, value, onChange }) {
  return (
    <div>
      <label className="mb-1 block text-sm text-slate-600">{label}</label>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full rounded-2xl border border-slate-300 bg-white p-2.5 text-slate-950"
      />
    </div>
  );
}

function FieldSelect({ label, value, onChange, options = [] }) {
  return (
    <div>
      <label className="mb-1 block text-sm text-slate-600">{label}</label>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-slate-300 bg-white p-2.5 text-slate-950"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {invoiceTypeLabel(opt)}
          </option>
        ))}
      </select>
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick, disabled = false, variant = "slate" }) {
  const cls =
    variant === "emerald"
      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
      : variant === "rose"
        ? "bg-rose-600 hover:bg-rose-700 text-white"
        : variant === "sky"
          ? "bg-sky-600 hover:bg-sky-700 text-white"
          : "bg-slate-900 hover:bg-slate-800 text-white";

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold disabled:opacity-50 ${cls}`}
      type="button"
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {label}
    </button>
  );
}